import { useCallback, useContext, useState } from "react"

import { parseNumber } from "@p-ban/common/src/utils/NumberUtils"

import type { EstimateErrors } from "../../../../../api/estimate/csComEst.d"
import isDeliveryZeroAvailable from "../../../service/deliveryZeroAvailabilityChecker"
import estimateField from "../../../service/estimateFields/estimateField"
import fillRestWithInitialValues from "../../../service/fillRestWithInitialValues"
import type { EventHandlers, Touched, CheckboxChangeEvent } from "../../../types"
import type { EstimateParams } from "../../../types/params"
import OneClickEstimateContext from "../../OneClickEstimateContext"
import getNoResetValues from "../getNoResetValues"
import updateValues from "../updateValues/updateValues"
import useNotify from "./useNotify"
import usePartsProcurement from "./usePartsProcurement"
import useSetInitialMaterialsArrivalDate from "./useSetInitialMaterialsArrivalDate"

const useEstimateForm = () => {
  const { valuesAndTouched, setValuesAndTouched, meta, bomSnapshotId, deliveryZeroFlg, mountQuickFlg } =
    useContext(OneClickEstimateContext)
  const { values, touched } = valuesAndTouched

  const [errors, setErrors] = useState<EstimateErrors>({})
  const [lastTouchedField, setLastTouchedField] = useState<string>()

  const setFieldValue = useCallback(
    // TODO 不適切な型の値を入れようとした場合にバリデーションしたい
    (name: string, value: string | boolean | Date | number | null) => {
      setLastTouchedField(name)
      setValuesAndTouched(({ values, touched }) => {
        return updateValues({ ...values, [name]: value }, { ...touched, [name]: true })
      })
    },
    [setValuesAndTouched],
  )

  const resetForm = useCallback(() => {
    setLastTouchedField(undefined)
    setValuesAndTouched({
      values: fillRestWithInitialValues({}),
      touched: {},
    })
  }, [setValuesAndTouched])

  const setDate = useCallback(
    (date: Date) => {
      setLastTouchedField(undefined)
      setValuesAndTouched(({ values, touched }) => {
        return updateValues({ ...values, [estimateField.materialsArrivalDate.key]: date }, touched)
      })
    },
    [setValuesAndTouched],
  )
  useSetInitialMaterialsArrivalDate(values, setDate)

  const { procurementValues } = usePartsProcurement(values)

  useNotify(values, lastTouchedField, touched)

  const params: EstimateParams = {
    values,
    procurementValues,
    meta,
    bomSnapshotId: bomSnapshotId ? Number(bomSnapshotId) : null,
    deliveryZeroFlg: isDeliveryZeroAvailable(values) ? deliveryZeroFlg : false,
    mountQuickFlg,
  }

  const handlers: EventHandlers = {
    kibanType: (event: React.ChangeEvent<HTMLInputElement>) => {
      setLastTouchedField(estimateField.kibanType.key)
      setValuesAndTouched(({ values: prevValues, touched: prevTouched }) => {
        const noResetValues = getNoResetValues(prevValues)

        const nextValues = fillRestWithInitialValues({
          [estimateField.kibanType.key]: event.target.value,
          ...noResetValues,
        })
        const nextTouched: Touched = { [estimateField.kibanType.key]: true }
        for (const noResetField of Object.keys(noResetValues) as (keyof typeof noResetValues)[]) {
          nextTouched[noResetField] = prevTouched[noResetField]
        }

        return { values: nextValues, touched: nextTouched }
      })
    },
    radio(event: React.ChangeEvent<HTMLInputElement>) {
      if (event.target.type !== "radio") throw new TypeError("イベントターゲットがラジオボタンではありません。")
      setFieldValue(event.target.name, event.target.value)
    },
    checkbox(event: React.ChangeEvent<HTMLInputElement> | CheckboxChangeEvent) {
      if (event.target.type !== "checkbox") throw new TypeError("イベントターゲットがチェックボックスではありません。")
      setFieldValue(event.target.name, event.target.checked)
    },
    select(event: React.ChangeEvent<HTMLSelectElement>) {
      setFieldValue(event.target.name, event.target.value)
    },
    selectAsNumber(e: React.ChangeEvent<HTMLSelectElement>) {
      const value = e.target.value === "" ? null : parseNumber(e.target.value, NaN)
      if (Number.isNaN(value)) throw new TypeError(`valueが数値または空文字ではありません。value: ${e.target.value}`)
      setFieldValue(e.target.name, value)
    },
    number(value: number | null, name: string) {
      setFieldValue(name, value)
    },
    date({ name, value }: { name: string; value: Date }) {
      setFieldValue(name, value)
    },
  } as const

  return {
    values,
    handlers,
    setFieldValue,
    resetForm,
    errors,
    setErrors,
    params,
  }
}

export default useEstimateForm
